import { isProduction } from '../constants';
import { getPortalCtaRedirects } from '../lendAPIConfig.types';

/**
 * Pre-React, client-side redirect that intercepts a portal's bare CTA URL
 * (e.g. `/yacht-loans/apply` on `www.yachtworld.com`) and forwards the visitor
 * to the configured LendAPI funnel target with all original query params
 * preserved verbatim.
 *
 * Runs synchronously before `firebase.initializeApp` and before
 * `ReactDOM.render`, so no Firestore loan is created for the redirected
 * request and no React/MobX setup is wasted.
 *
 * @returns `true` if a redirect was triggered (caller should short-circuit
 *   further bootstrapping); `false` otherwise.
 */
export function maybeRedirectToLendAPI(): boolean {
  const env: 'qa' | 'production' = isProduction() ? 'production' : 'qa';
  const { hostname, pathname, search } = window.location;

  const entry = getPortalCtaRedirects(env).find(
    (e) =>
      e.hostname === hostname &&
      e.enabled === true &&
      (pathname === e.pathPrefix || pathname === `${e.pathPrefix}/`)
  );
  if (!entry) {
    return false;
  }

  const params = new URLSearchParams(search);

  if (entry.requireQueryParams) {
    const requiredEntries = Object.entries(entry.requireQueryParams);
    if (requiredEntries.length > 0) {
      const allMatch = requiredEntries.every(([key, expected]) => {
        const actual = params.get(key);
        return actual !== null && actual.toLowerCase() === expected.toLowerCase();
      });
      if (!allMatch) {
        return false;
      }
    }
  }

  if (params.get('skipRedirect') === 'true') {
    return false;
  }

  if (entry.prodTestingInQA === true) {
    params.set('prodTesting', 'true');
  } else {
    params.delete('prodTesting');
  }

  const serialized = params.toString();
  const queryString = serialized ? `?${serialized}` : '';
  const { productType, productId, productSlug } = entry.target;
  const targetUrl = `${entry.pathPrefix}/loan-application/${productType}/${productId}/${productSlug}/${queryString}`;

  if (!targetUrl.startsWith('/') || targetUrl.startsWith('//')) {
    return false;
  }

  window.location.replace(targetUrl);
  return true;
}
